import { getAccessToken } from '../authentication/supabaseService';


export async function clearAllRevenue(restaurantId: string): Promise<{ success: boolean; deletedCount: number; message: string }> {
  try {
    const token = await getAccessToken();
    const response = await fetch(`/api/automation/clear-revenue/${restaurantId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to clear revenue history');
    }

    return await response.json();
  } catch (error: any) {
    console.error('Error clearing revenue history:', error);
    throw new Error(error.message || 'Failed to clear revenue history');
  }
}

export async function clearAllOrders(restaurantId: string): Promise<{ success: boolean; deletedCount: number; message: string }> {
  try {
    const token = await getAccessToken();
    const response = await fetch(`/api/automation/clear-orders/${restaurantId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to clear order history');
    }

    return await response.json();
  } catch (error: any) {
    console.error('Error clearing order history:', error);
    throw new Error(error.message || 'Failed to clear order history');
  }
}

export async function clearRevenueBySourceIds(revenueSourceIds: string[]): Promise<{ success: boolean; deletedCount: number; message: string }> {
  try {
    const token = await getAccessToken();
    const response = await fetch('/api/automation/clear-revenue-sources', {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ revenueSourceIds }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to clear revenue history');
    }

    return await response.json();
  } catch (error: any) {
    console.error('Error clearing revenue history:', error);
    throw new Error(error.message || 'Failed to clear revenue history');
  }
}

/** Gap on one generated stream: where its data currently ends and how far it is behind. */
export interface DemoExtendStreamGap {
  lastDate: string | null;
  daysToGenerate: number;
}

/** Section selector meaning "every section of this restaurant" — mirrors the server constant. */
export const ALL_SECTIONS = 'ALL';

export type DemoExtendBlockedReason = 'NO_COST_PRICE_ANALYSES' | 'UP_TO_DATE';

/** One section's own gap. `canExtend` describes THIS section, not the whole run. */
export interface DemoExtendSectionPreview {
  sectionId: string;
  sectionName: string;
  canExtend: boolean;
  reason?: DemoExtendBlockedReason;
  revenue: DemoExtendStreamGap;
  orders: DemoExtendStreamGap;
}

export interface DemoExtendPreview {
  canExtend: boolean;
  /** Only set when canExtend is false. */
  reason?: DemoExtendBlockedReason;
  /** One entry per targeted section — a single entry unless ALL_SECTIONS was requested. */
  sections: DemoExtendSectionPreview[];
  /** Restaurant-wide, so it is reported once however many sections are targeted. */
  inventory: { monthsMissing: number };
}

export interface DemoExtendSectionResult {
  sectionId: string;
  sectionName: string;
  revenueDaysGenerated: number;
  orderDaysGenerated: number;
  warnings: string[];
}

export interface DemoExtendResult {
  success: boolean;
  warnings: string[];
  /** Totals across every section the run touched. */
  revenueDaysGenerated: number;
  orderDaysGenerated: number;
  inventorySessionsCreated: number;
  sections: DemoExtendSectionResult[];
}

export async function fetchDemoExtendPreview(restaurantId: string, sectionId: string): Promise<DemoExtendPreview> {
  try {
    const token = await getAccessToken();
    const query = new URLSearchParams({ restaurantId, sectionId });
    const response = await fetch(`/api/automation/demo-automation/extend-preview?${query}`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.details || errorData.error || 'Failed to load demo extend preview');
    }

    return await response.json();
  } catch (error: any) {
    console.error('Error loading demo extend preview:', error);
    throw new Error(error.message || 'Failed to load demo extend preview');
  }
}

export async function extendDemoData(restaurantId: string, sectionId: string): Promise<DemoExtendResult> {
  try {
    const token = await getAccessToken();
    const response = await fetch('/api/automation/demo-automation/extend', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ restaurantId, sectionId }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.details || errorData.error || 'Failed to extend demo data');
    }

    return await response.json();
  } catch (error: any) {
    console.error('Error extending demo data:', error);
    throw new Error(error.message || 'Failed to extend demo data');
  }
}

export async function clearOrdersBySectionIds(sectionIds: string[]): Promise<{ success: boolean; deletedCount: number; message: string }> {
  try {
    const token = await getAccessToken();
    const response = await fetch('/api/automation/clear-order-sections', {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ sectionIds }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to clear order history');
    }

    return await response.json();
  } catch (error: any) {
    console.error('Error clearing order history:', error);
    throw new Error(error.message || 'Failed to clear order history');
  }
}
